import { NgIf } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { HeightWeightInputComponent } from '@components/consultation-request/height-weight-input/height-weight-input.component';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { DiagnosisAnswers } from '@enums/diagnosis-answers';
import { ConsultationStorageService } from '@services/consultation-storage.service';

@Component({
  selector: 'app-bmi-calculator',
  templateUrl: './bmi-calculator.component.html',
  imports: [NgIf, HeightWeightInputComponent],
})
export class BmiCalculatorComponent implements OnInit {
  @Input() public form: FormGroup;

  private consultationStorageService = inject(ConsultationStorageService);

  /**
   * Gets the disqualified status of the user.
   */
  public get disqualified(): boolean {
    return this.bmiScore < this.minimumQualifyingBmiScore;
  }

  /**
   * Gets the height in feet from the control.
   */
  public get heightFeet(): number {
    return this.form.get('height_feet').value;
  }

  /**
   * Gets the height in inches from the control.
   */
  public get heightInches(): number {
    return this.form.get('height_inches').value;
  }

  /**
   * Calculate the ideal weight based on the BMI score and height.
   */
  public get idealWeight(): number {
    return this.calculateWeightFromBmi(this.minimumQualifyingBmiScore, this.height);
  }

  /**
   * Gets the answers to the diagnosis question.
   */
  private get questionDiagnosisAnswers() {
    const diagnosisQuestionId = 144;
    const glpAnswers = this.consultationStorageService.getQuestionnaireAnswers(ConsultationTreatmentTypes.Glp1);

    return glpAnswers?.[diagnosisQuestionId]?.[0];
  }

  /**
   * Determines the qualified BMI score based on the answers to the diagnosis question.
   * If the user answers "No, none of these apply to me" to the diagnosis question, the
   * qualified BMI score is 28. Otherwise, the qualified BMI score is 26.
   */
  private get minimumQualifyingBmiScore(): number {
    const BMI_SCORE = {
      NO_SYMPTOMS: 28,
      DEFAULT: 26,
    } as const;

    return this.questionDiagnosisAnswers === DiagnosisAnswers.NONE_APPLY || !this.questionDiagnosisAnswers
      ? BMI_SCORE.NO_SYMPTOMS
      : BMI_SCORE.DEFAULT;
  }

  /**
   * Calculate the height in inches.
   */
  private get height(): number {
    return this.heightFeet * 12 + this.heightInches;
  }

  /**
   * Gets the weight in pounds from the control.
   */
  private get weight(): number {
    return this.form.get('weight').value;
  }

  /**
   * Calculate the BMI score based on the weight and height.
   */
  private get bmiScore(): number {
    return this.calculateBmiScore(this.weight, this.height);
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.addHeightAndWeightValidators();
    this.subscribeToHeightAndWeightChanges();
  }

  /**
   * Handles the change of the weight or height value.
   */
  private handleInputChange(): void {
    if (this.weight && this.heightFeet && this.heightInches !== null) {
      this.consultationStorageService.bmiScore = this.bmiScore;
    }
  }

  /**
   * Adds the height and weight validators to the form controls.
   */
  private addHeightAndWeightValidators(): void {
    this.form.get('height_feet').addValidators(Validators.required);
    this.form.get('height_inches').addValidators(Validators.required);
    this.form.get('weight').addValidators(Validators.required);
  }

  /**
   * Subscribes to the height and weight value changes.
   */
  private subscribeToHeightAndWeightChanges(): void {
    this.form.get('height_feet').valueChanges.subscribe(() => this.handleInputChange());
    this.form.get('height_inches').valueChanges.subscribe(() => this.handleInputChange());
    this.form.get('weight').valueChanges.subscribe(() => this.handleInputChange());
  }

  /**
   * Calculate the BMI score based on the weight and height.
   * @param {number} weight the weight of the patient in pounds
   * @param {number} height the height of the patient in inches
   */
  private calculateBmiScore(weight: number, height: number): number {
    return Math.round((weight / Math.pow(height, 2)) * 703 * 10) / 10;
  }

  /**
   * Calculate the weight based on the BMI score and height.
   * @param {number} bmi    the BMI score
   * @param {number} height the height of the patient in inches
   */
  private calculateWeightFromBmi(bmi: number, height: number): number {
    return Math.round((bmi * Math.pow(height, 2)) / 703);
  }
}
